
import { defineComponent, ref, reactive, watch, computed, Ref } from "vue";
import $API from "@/services";
import FileUploader from "@/components/common/FileUploader";
import Textarea from "primevue/textarea";
import { IAlert } from "@/models/common";
import { IDoctor, PossibleTypes } from "@/models/doctor";
import { useStore } from "@/store";
import { CommonMutationTypes } from "@/store/modules/common/mutations";
import { IRequest, RequestStatuses, RequestKindTypes } from "@/models/request";
import { IFileListItem } from "@/models/file";
import FileList from "@/components/file/FileList.vue";
import { DataType, FileType } from "@/services/file";
import Doctors from "@/components/common/Doctors/Doctors";
import { IFromValidate, ISecondOpinionPurpose, SecondOpinion } from "@/models/secondOpinion";
import router from "@/router";
import { Form } from "vee-validate";
import { useI18n } from "vue-i18n";
import { messageType } from "@/models/message";
import PageFrame from "@/components/common/PageFrame.vue";
import Button from "primevue/button";
import { getDoctor, getFacilityDoctorList } from "@/services/doctor";
import DoctorItemOptions from "@/components/common/Doctors/DoctorItemOptions.vue";
import { useConfirm } from "primevue/useconfirm";
import CustomConfirmDialog from "@/components/primevueCustom/ConfirmDialog.vue";

enum PageStates {
  "DOCTOR",
  "PURPOSE",
}
export default defineComponent({
  name: "Second Opinion Request Edit",
  props: {
    id: String,
  },
  setup(props) {
    const state = reactive({
      id: props.id,
      purpose: {
        doctorKey: null as Number | null,
        other: "",
        files: null as Array<IFileListItem> | null,
      } as ISecondOpinionPurpose,
      requestStatus: null as RequestStatuses | null,
      selectFile: null as IFileListItem | null,
      isLoading: true,
      message: "",
      page: PageStates.PURPOSE,
      oldDoctorKey: null as Number | null,
      selectedHospital: null as null | Number,
    });

    const store = useStore();

    const formValidate: IFromValidate = reactive({
      doctorKey: false,
      other: false,
    });

    const confirm = useConfirm();
    const isConfirmButton = () => {
      confirm.require({
        icon: "pi pi-exclamation-triangle",
        acceptLabel: t("ok"),
        group: "isConfirm",
        message: t("update is completed"),
        accept: () => {
          goBack();
        },
        reject: () => {
          goBack();
        },
      });
    };

    const doctors$: Ref<Doctors | null> = ref(null);
    const fileUploader$: Ref<FileUploader | null> = ref(null);
    const doctors: Ref<IDoctor> = ref({} as IDoctor);
    const tempDoctors: Ref<IDoctor> = ref({} as IDoctor);

    watch(
      () => state.purpose,
      (n: ISecondOpinionPurpose) => {
        (formValidate.doctorKey = !!n.doctorKey), (formValidate.other = !!n.other);
      },
      { deep: true }
    );

    const url = computed(() => {
      return `${$API.BaseURL}/api/ApiFileUploadV2/Upload?reftab=request&refkey=${state.id}`;
    });

    const filesCount = computed(() => {
      return state.purpose.files?.filter((f) => f.PrescriptionFlag !== 1 && f.FileStatus === 0)
        .length;
    });

    const isEditable = computed(() => {
      return (
        !state.id ||
        ((state.requestStatus ?? 100) >= RequestStatuses.Preparing &&
          (state.requestStatus ?? 100) < RequestStatuses.Scheduled)
      );
    });

    const { t } = useI18n({ useScope: "global" });

    const alert: Ref<IAlert> = ref({
      type: messageType.ERROR,
      content: "",
    });

    const doctorKeyValidate = computed(() => formValidate.doctorKey);
    const otherValidate = computed(() => formValidate.other);

    const goBack = () => {
      router.go(-1);
    };

    return {
      state,
      props,
      store,
      DataType,
      url,
      isEditable,
      formValidate,
      FileType,
      alert,
      t,
      filesCount,
      RequestKindTypes,
      PossibleTypes,
      doctors$,
      fileUploader$,
      doctorKeyValidate,
      otherValidate,
      doctors,
      PageStates,
      isConfirmButton,
      goBack,
      tempDoctors,
    };
  },
  async mounted() {
    await this.onSetData();
  },
  methods: {
    async saveConfirm() {
      this.$confirm.require({
        header: this.t("ReceptionSaveHeader"),
        acceptLabel: this.t("Yes"),
        rejectLabel: this.t("No"),
        group: "default",
        accept: async () => {
          //callback to execute when user confirms the action
          await this.onSave();
        },
        reject: () => {
          //callback to execute when user rejects the action
        },
      });
    },

    cancelConfirm() {
      if (!this.isEditable) this.goBack();
      else {
        this.$confirm.require({
          header: this.t("CalcelHeader"),
          acceptClass: "p-button-danger",
          acceptLabel: this.t("Yes"),
          rejectLabel: this.t("No"),
          group: "default",
          accept: () => {
            this.goBack();
          },
          reject: () => {},
        });
      }
    },

    onSelectedDoctor(doctor: IDoctor) {
      this.tempDoctors = doctor;
    },

    changedDoctor() {
      this.state.purpose.doctorKey = this.tempDoctors.selectedDoctor[0].DoctorKey;
      this.doctors = this.tempDoctors;
    },

    async deletedFile() {
      await this.onSetData();
    },
    filesProgress() {
      if (!this.store.getters.getIsFileUpdate) {
        setTimeout(() => {
          this.isConfirmButton();
        }, 1000);
      }
    },
    async onSetData() {
      try {
        this.state.isLoading = true;
        this.store.commit(CommonMutationTypes.SET_IS_LODING, true);
        if (!!this.state.id && Number.parseInt(this.state.id)) {
          await this.getRequest(this.state.id);
          await this.getFiles(this.state.id);
        }
        await this.getDoctorList();

        const doctor = await getDoctor(this.state.purpose.doctorKey?.toString() ?? "0");
        if (doctor) {
          this.doctors.selectedDoctor = [doctor];

          const facilityKey = doctor?.FacilityKey ?? 0;
          this.state.selectedHospital = facilityKey;

          if (facilityKey.valueOf() > 0) {
            this.doctors.selectedFacility = await $API.FacilityService.getFacility(
              facilityKey.valueOf()
            );
          }
        }

        this.doctors.doctorList = await getFacilityDoctorList(
          PossibleTypes.SECOND_OPINION,
          doctor?.FacilityKey ?? 0
        );
        this.doctors.doctorListSplit = [this.doctors.doctorList];
        this.doctors.selectedPage = 0;
        this.doctors.displayDoctorRow = 0;

        console.log(this.doctors, "SecondOpinionRequestEdit.doctors");
        console.log(this.filesCount, "SecondOpinionRequestEdit.filesCount");
      } catch (e) {
        console.error(e);
        this.store.commit(CommonMutationTypes.FILE_UPDATE, false);
      } finally {
        this.store.commit(CommonMutationTypes.SET_IS_LODING, this.store.getters.getIsFileUpdate);
        this.state.isLoading = false;
      }
    },

    saveValidate() {
      return this.formValidate.doctorKey && this.formValidate.other;
    },

    async onSave() {
      try {
        this.store.commit(CommonMutationTypes.SET_IS_LODING, true);
        if (!this.saveValidate()) {
          this.alert = {
            content: "validateError",
            type: messageType.ERROR,
          };

          this.store.commit(CommonMutationTypes.SET_IS_LODING, false);
          return;
        }

        const requestKey = !this.state.id ? 0 : Number.parseInt(this.state.id);
        const saveData = new SecondOpinion({
          doctorkey: this.state.purpose.doctorKey?.valueOf() ?? 0,
          other: this.state.purpose.other,
          requestKey,
        });

        const request = await $API.RequestService.save(saveData);
        if (request) {
          this.state.id = request.RequestKey + "";
          this.state.requestStatus = request.RequestStatus;

          if (this.fileUploader$ && this.fileUploader$?.getFilesCount() > 0)
            await this.fileUploader$?.upload(this.url);
          else {
            this.store.commit(CommonMutationTypes.SET_IS_LODING, false);
            this.isConfirmButton();
          }
        }
      } catch (e) {
        console.error(e);

        this.alert = {
          content: this.t("failed to save"),
          type: messageType.ERROR,
        };
      } finally {
        this.store.commit(CommonMutationTypes.SET_IS_LODING, false);
      }
    },

    getImagePath(filePath: string) {
      return $API.FileService.getFilePath(filePath);
    },

    getPdfPath(filePath: string) {
      return $API.FileService.getPdfPath(filePath);
    },

    submit(e: Event) {
      e.preventDefault();
    },
    async getRequest(id: string) {
      const request = await $API.RequestService.getRequest(Number.parseInt(id));
      if (request && !Array.isArray(request)) {
        this.state.purpose.other =
          request.ChiefComplaint?.toString() ?? request.PurposeOthers?.toString() ?? "";
        this.state.requestStatus = request.RequestStatus ?? null;
        await this.getRequestDoctor(request);
      }
    },

    async getFiles(id: string) {
      const ResultData = await $API.FileService.getFileList(id);
      if (!Array.isArray(ResultData)) return;
      this.state.purpose.files = ResultData;
    },

    async getRequestDoctor(resultData: IRequest) {
      this.state.purpose.doctorKey = resultData.DesiredDrKey;
    },
    async getDoctorList() {
      await this.doctors$?.onSetData();
    },
  },

  components: {
    FileUploader,
    Textarea,
    FileList,
    Doctors,
    Form,
    Button,
    PageFrame,
    CustomConfirmDialog,
    DoctorItemOptions,
  },
});
